import React, { useEffect, useState } from 'react'
import { org_nodes_path, org_node_path } from '@/lib/routes'
import CountryFields, { TimeZoneOption } from './CountryFields'
import { Button } from '@/components/ui/Button'

export interface OrgNodeData {
  id: string
  parent_id: string | null
  name: string
  slug: string
  depth: number
  position: number
  currency: string | null
  time_zone: string | null
  sales_channel: boolean
  wholesale: boolean
}

interface Props {
  node: OrgNodeData | null
  parentNode: OrgNodeData | null
  mode: 'edit' | 'add-child'
  /** Currency codes accepted by the server (OrgNode::CURRENCIES). */
  currencies: string[]
  /** Time zones accepted by the server (OrgNode.time_zone_options). */
  timeZones: TimeZoneOption[]
  onClose: () => void
  onSaved: (node: OrgNodeData) => void
  onDeleted: (id: string) => void
}

export default function NodeEditPanel({ node, parentNode, mode, currencies, timeZones, onClose, onSaved, onDeleted }: Props) {
  // Country level = direct child of the root; the only nodes that carry a currency.
  const isCountry = mode === 'edit' ? node?.depth === 2 : parentNode?.parent_id === null
  const [name, setName] = useState(node?.name ?? '')
  const [slug, setSlug] = useState(node?.slug ?? '')
  const [currency, setCurrency] = useState(node?.currency ?? currencies[0] ?? 'VND')
  const [timeZone, setTimeZone] = useState(node?.time_zone ?? '')
  const [salesChannel, setSalesChannel] = useState(node?.sales_channel ?? false)
  const [wholesale, setWholesale] = useState(node?.wholesale ?? false)
  const [errors, setErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setName(node?.name ?? '')
    setSlug(node?.slug ?? '')
    setCurrency(node?.currency ?? currencies[0] ?? 'VND')
    setTimeZone(node?.time_zone ?? '')
    setSalesChannel(node?.sales_channel ?? false)
    setWholesale(node?.wholesale ?? false)
    setErrors([])
  }, [node, mode])

  const title =
    mode === 'edit' ? 'Edit Node' :
    `Add child node to "${parentNode?.name}"`

  function handleSave() {
    setSaving(true)
    setErrors([])

    const payload = {
      org_node: {
        name,
        ...(mode === 'edit' ? { slug } : {}),
        sales_channel: salesChannel,
        wholesale,
        ...(isCountry ? { currency, time_zone: timeZone || null } : {}),
        ...(mode === 'add-child' && parentNode ? { parent_id: parentNode.id } : {}),
      },
    }

    const request = mode === 'edit' && node
      ? fetch(org_node_path(node.id), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
          body: JSON.stringify(payload),
        })
      : fetch(org_nodes_path(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken() },
          body: JSON.stringify(payload),
        })

    request
      .then(async (res) => {
        const data = await res.json()
        if (res.ok) { onSaved(data); onClose() }
        else setErrors(data.errors ?? [data.error])
      })
      .catch(() => setErrors(['Network error. Please try again.']))
      .finally(() => setSaving(false))
  }

  function handleDelete() {
    if (!node) return
    if (!confirm(`Delete "${node.name}"? This cannot be undone.`)) return

    fetch(org_node_path(node.id), {
      method: 'DELETE',
      headers: { 'X-CSRF-Token': csrfToken() },
    }).then(async (res) => {
      if (res.ok) { onDeleted(node.id); onClose() }
      else {
        const data = await res.json()
        setErrors(data.errors ?? [data.error])
      }
    }).catch(() => setErrors(['Network error. Please try again.']))
  }

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="w-[420px] max-w-[90vw] bg-white rounded-lg shadow-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-sm">{title}</h3>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-6 w-6 text-lg leading-none text-muted-foreground hover:text-muted-foreground">×</Button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {errors.length > 0 && (
            <div className="bg-destructive-foreground border border-destructive rounded-md px-3 py-2 text-sm text-destructive">
              {errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Name</label>
            <input
              className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              autoFocus
            />
          </div>
          {mode === 'edit' && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Slug</label>
              <input
                className="w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Used in URLs. Leave blank to regenerate from the name.
              </p>
            </div>
          )}
          {mode === 'add-child' && parentNode && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Parent</label>
              <div className="px-3 py-2 text-sm bg-muted border border-border rounded-md text-foreground">
                {parentNode.name}
              </div>
            </div>
          )}
          {isCountry && (
            <CountryFields
              currency={currency}
              currencies={currencies}
              onCurrencyChange={setCurrency}
              timeZone={timeZone}
              timeZones={timeZones}
              onTimeZoneChange={setTimeZone}
            />
          )}
          <div>
            <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Channel type</label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 accent-primary"
                checked={salesChannel}
                onChange={(e) => setSalesChannel(e.target.checked)}
              />
              <span>
                Sales channel
                <span className="block text-xs text-muted-foreground">Sales are recorded here. Leaf sales channels (no sales-flagged child) are the forecast units; a flagged parent becomes a grouping/rollup node.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
              <input
                type="checkbox"
                className="mt-0.5 accent-primary"
                checked={wholesale}
                onChange={(e) => setWholesale(e.target.checked)}
              />
              <span>
                Wholesale channel
                <span className="block text-xs text-muted-foreground">Plans in Wholesale Forecast only. Leaves By Month, By Day and the Monthly Plan task; keeps Products & Pricings for the target ASP.</span>
              </span>
            </label>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
          {mode === 'edit' && node && node.parent_id !== null ? (
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              Delete node
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}
